import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/TopNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { authService } from '../../services/authService';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePassword = (password) => {
    return password?.length >= 8;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (authError) {
      setAuthError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData?.password)) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setAuthError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await authService?.signIn(formData?.email, formData?.password);
      navigate('/admin-dashboard');
    } catch (error) {
      setAuthError(error?.message || 'Invalid credentials. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <TopNavigation />
      <div className="content-area">
        <div className="min-h-[calc(100vh-80px)] flex items-center justify-center py-8 md:py-12 lg:py-16">
          <div className="w-full max-w-md px-4 md:px-6">
            <div className="bg-card border border-border rounded-xl p-6 md:p-8 lg:p-10 shadow-prominent">
              <div className="flex flex-col items-center mb-6 md:mb-8">
                <div className="w-14 h-14 md:w-16 md:h-16 lg:w-18 lg:h-18 rounded-xl flex items-center justify-center mb-4 md:mb-5" style={{ background: 'rgba(99, 102, 241, 0.1)' }}>
                  <Icon name="Shield" size={32} color="var(--color-secondary)" />
                </div>
                <h1 className="text-2xl font-semibold text-foreground text-center mb-2">
                  Admin Access
                </h1>
                <p className="text-sm md:text-base text-muted-foreground text-center">
                  Secure authentication for content management
                </p>
              </div>

              {authError && (
                <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{authError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                <Input
                  type="email"
                  name="email"
                  label="Email Address"
                  placeholder="email@example.com"
                  value={formData?.email}
                  onChange={handleInputChange}
                  error={errors?.email}
                  required
                  disabled={isLoading}
                />

                <Input
                  type="password"
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                  value={formData?.password}
                  onChange={handleInputChange}
                  error={errors?.password}
                  required
                  disabled={isLoading}
                />

                <Button
                  type="submit"
                  variant="secondary"
                  size="lg"
                  fullWidth
                  loading={isLoading}
                  disabled={isLoading}
                  iconName="LogIn"
                  iconPosition="right"
                  className="mt-6 md:mt-8"
                >
                  {isLoading ? 'Authenticating...' : 'Sign In'}
                </Button>
              </form>

              {/* Demo credentials section removed for production */}
            </div>

            <div className="mt-6 md:mt-8 text-center">
              <button
                onClick={() => navigate('/homepage')}
                className="inline-flex items-center gap-2 text-sm md:text-base text-muted-foreground hover:text-foreground transition-colors duration-250"
              >
                <Icon name="ArrowLeft" size={18} color="currentColor" />
                Back to Homepage
              </button>
            </div>
          </div>
        </div>
      </div>
      <footer className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                <Icon name="Zap" size={20} color="var(--color-primary)" />
              </div>
              <span className="text-sm md:text-base font-medium text-foreground">Sleeve</span>
            </div>
            <p className="text-xs md:text-sm text-muted-foreground">
              &copy; {new Date()?.getFullYear()} Sleeve. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdminLogin;